import { useQuery } from "@tanstack/react-query"
import { client } from "@/api"

// A5 (observability-product-funnels): in-product analytics for the admin stats panel. Calls the
// admin backend endpoint (which runs a FIXED set of whitelisted HogQL queries server-side with
// the PostHog personal API key held as a Worker secret). This hook NEVER touches PostHog directly
// and never sees the key — it only consumes the aggregate card payload.

export interface FunnelStep {
  event: string
  persons: number
  conversionFromPrev: number
  conversionFromStart: number
}

export interface FunnelCard {
  ok: boolean
  error?: string
  steps: FunnelStep[]
}

// #1500 — the `platform` breakdown (web | ios | chopper | backend, plus an "(untagged)" bucket
// that holds chopper and every event ingested before the property shipped).
export interface PlatformFunnel {
  platform: string
  steps: FunnelStep[]
}

export interface PlatformFunnelsCard {
  ok: boolean
  error?: string
  platforms: PlatformFunnel[]
  platformNote: string
}

export interface EventVolume {
  event: string
  occurrences: number
  persons: number
}

export interface VolumesCard {
  ok: boolean
  error?: string
  events: EventVolume[]
}

export interface EventVolumeByPlatform {
  event: string
  platforms: Array<{ platform: string; occurrences: number; persons: number }>
}

export interface PlatformVolumesCard {
  ok: boolean
  error?: string
  events: EventVolumeByPlatform[]
  platformNote: string
}

export interface MixSlice {
  value: string
  persons: number
}

export interface MixCard {
  ok: boolean
  error?: string
  slices: MixSlice[]
}

export interface CaptureModeShare {
  mode: string
  occurrences: number
  pct: number
}

export interface CoverageRow {
  event: string
  total: number
  identifiedPct: number
  shares: CaptureModeShare[]
}

export interface CoverageCard {
  ok: boolean
  error?: string
  rows: CoverageRow[]
  cfDenominatorNote: string
}

export interface ProductStats {
  generated_at: string
  environment: string
  window_days: number
  configured: boolean
  activation: FunnelCard
  /** Every client blended together (incl. the server-side transcode leg). */
  upload: FunnelCard
  /** The client-attributable upload steps, split by platform (#1500). */
  uploadByPlatform: PlatformFunnelsCard
  volumes: VolumesCard
  volumesByPlatform: PlatformVolumesCard
  deviceRoleMix: {
    device: MixCard
    os: MixCard
    role: MixCard
  }
  coverage: CoverageCard
}

// `window` is in DAYS (default 90). The endpoint always returns HTTP 200; when the personal API
// key secret is unset the body carries `configured:false` (empty cards) and the page renders a
// "not configured" banner. (We can't rely on a non-2xx status here: the admin RPC client in
// src/api/index.ts throws an APIError on any non-ok response, which would bypass this body.)
export function useProductStats(windowDays = 90) {
  return useQuery({
    queryKey: ["admin", "product-stats", windowDays],
    queryFn: async () => {
      const response = await client.admin.observability["product-stats"].$get({
        query: { window: windowDays },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch product stats")
      }
      return (await response.json()) as ProductStats
    },
    refetchInterval: 5 * 60 * 1000,
  })
}
